import { cn } from "@faire/prototype-playground/lib/utils";
import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { ChevronLeft } from "@faire/slate-icons/ChevronLeft";
import { Pin } from "@faire/slate-icons/Pin";
import { Search } from "@faire/slate-icons/Search";
import { StarFilled } from "@faire/slate-icons/StarFilled";
import { StarHalf } from "@faire/slate-icons/StarHalf";
import { StarOutlined } from "@faire/slate-icons/StarOutlined";
import { IconButton } from "@faire/slate/components/icon-button";
import React, { useState } from "react";

import type { PlacesSuggestion, StoreProfile } from "./data";

// Serif treatment used for signup display headlines (Slate web typography
// "displaySSerifRegular"). Matches the production signup form.
export const SERIF_FONT = '"Nantes_fix", Georgia, serif';

export const FaireWordmark: React.FC<{ className?: string }> = ({
  className = "h-5 w-auto object-contain",
}) => (
  <img
    alt="Faire"
    src="https://cdn.faire.com/static/logo.svg"
    className={className}
    onError={(event) => {
      (event.target as HTMLImageElement).src =
        "https://cdn.faire.com/static/logo.svg";
    }}
  />
);

// Google's multi-color "G" mark, used on the "Sign up with Google" button.
export const GoogleGlyph: React.FC<{ className?: string }> = ({
  className = "h-5 w-5",
}) => (
  <svg viewBox="0 0 48 48" role="img" aria-hidden className={className}>
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

// Mirrors production's `SignUpStepLayout` container so every screen in the
// prototype breathes the same way: the title clears the progress bar, and each
// region below it is separated by one section gap.
const shellColumnStyle =
  "m-auto flex w-full flex-col gap-(--slate-spacing-section-default) px-(--slate-dimensions-6) py-(--slate-dimensions-8) md:max-w-[720px] md:px-[120px] md:py-(--slate-dimensions-16)";

interface SignupShellProps {
  progressPercent: number;
  title: string;
  subcopy?: React.ReactNode;
  /** When provided, renders a back chevron at the top-left of the header. */
  onBack?: () => void;
  children: React.ReactNode;
}

// Faire wordmark + thin progress bar + the production signup column. Mirrors
// the real retailer signup chrome so the magic steps read as part of the flow.
export const SignupShell: React.FC<SignupShellProps> = ({
  progressPercent,
  title,
  subcopy,
  onBack,
  children,
}) => {
  return (
    <div className="bg-fs-surface-primary min-h-screen">
      <header className="relative flex h-16 w-full items-center justify-center">
        {onBack ? (
          <div className="absolute top-1/2 left-2 -translate-y-1/2">
            <IconButton
              Icon={ChevronLeft}
              variant="plain"
              size="small"
              shape="circle"
              aria-label="Go back"
              onClick={onBack}
            />
          </div>
        ) : null}
        <FaireWordmark />
      </header>
      <div className="bg-fs-surface-secondary h-1 w-full">
        <div
          className="h-full bg-(--slate-color-action-surface-default) transition-[width] duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className={shellColumnStyle}>
        <div className="flex flex-col gap-(--slate-dimensions-3)">
          <h1
            className="text-fs-text-primary text-[30px] leading-[38px]"
            style={{ fontFamily: SERIF_FONT }}
          >
            {title}
          </h1>
          {subcopy ? (
            <p className="type-fs-paragraph text-fs-text-subdued">{subcopy}</p>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
};

// Small "sparkle" mark used to signal the magic / auto-filled moments.
export const SparkleIcon: React.FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    role="img"
    aria-hidden
    className={className}
  >
    <path
      d="M12 2.5l1.9 5.3a4 4 0 0 0 2.4 2.4l5.3 1.9-5.3 1.9a4 4 0 0 0-2.4 2.4L12 21.7l-1.9-5.3a4 4 0 0 0-2.4-2.4L2.4 12l5.3-1.9a4 4 0 0 0 2.4-2.4L12 2.5z"
      fill="currentColor"
    />
  </svg>
);

// Sparkle + text, the shared treatment for every "we did this for you" line.
export const SparkleNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="text-fs-text-subdued flex items-start gap-(--slate-dimensions-2)">
    <SparkleIcon className="mt-[3px] h-4 w-4 shrink-0" />
    <span>{children}</span>
  </span>
);

// Open design question parked next to the screen it belongs to. Dashed border
// and an explicit label so it never reads as part of the signup UI.
export const DesignNote: React.FC<{
  title?: string;
  children: React.ReactNode;
}> = ({ title = "Open question", children }) => (
  <div className="border-fs-border-muted rounded-[4px] border border-dashed p-(--slate-spacing-element-default)">
    <p className="type-fs-label-medium text-fs-text-subdued mb-(--slate-dimensions-1) tracking-wide uppercase">
      {title}
    </p>
    <p className="type-fs-label text-fs-text-subdued">{children}</p>
  </div>
);

// Renders a 0–5 aggregate rating as five star glyphs, using half stars for the
// fractional part. Colors come from the icon's default (primary) token.
export const StarRating: React.FC<{ rating: number; className?: string }> = ({
  rating,
  className = "h-3.5 w-3.5",
}) => (
  <span
    className="flex items-center gap-[1px]"
    aria-label={`${rating} out of 5 stars`}
  >
    {[0, 1, 2, 3, 4].map((index) => {
      const remainder = rating - index;
      const Icon =
        remainder >= 0.75
          ? StarFilled
          : remainder >= 0.25
            ? StarHalf
            : StarOutlined;
      return <Icon key={index} className={className} aria-hidden />;
    })}
  </span>
);

// A single collage cell: fills its (relative) parent exactly and clips, so a
// tall photo can never overflow the fixed-height collage. Falls back to a
// deterministic placeholder if the remote image fails to load.
const PlacesPhoto: React.FC<{
  src: string;
  alt: string;
  fallbackSeed: string;
  className?: string;
}> = ({ src, alt, fallbackSeed, className }) => (
  <div className={`relative overflow-hidden ${className ?? ""}`}>
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover"
      onError={(event) => {
        const image = event.target as HTMLImageElement;
        if (!image.dataset.fallback) {
          image.dataset.fallback = "1";
          image.src = `https://picsum.photos/seed/${fallbackSeed}/480/360`;
        }
      }}
    />
  </div>
);

const placesCardStyle = "overflow-hidden rounded-lg bg-fs-surface-secondary";
const placesInfoStyle =
  "flex flex-col gap-(--slate-dimensions-1) p-(--slate-spacing-element-default)";
const placesRatingRowStyle = "flex items-center gap-(--slate-dimensions-1)";
const placesMetadataStyle = "type-fs-paragraph text-fs-text-subdued";

// Google-Places-style confirm card: a photo collage, the business name, and an
// aggregate rating line. Mirrors the Figma "We found a match!" card
// (node 465:6027), adapted to Slate tokens.
export const StorePlacesCard: React.FC<{
  profile: StoreProfile;
}> = ({ profile }) => {
  const [primary, ...rest] = profile.photos ?? [];
  const cityAndState = [profile.city, profile.state].filter(Boolean).join(", ");
  const fullLocation = [cityAndState, profile.postalCode]
    .filter(Boolean)
    .join(" ");
  // One quiet line carries both disambiguators (which location, which site) so
  // the card stays three lines tall instead of five labeled rows.
  const identityLine = [fullLocation, profile.website]
    .filter(Boolean)
    .join(" · ");

  if (!primary) {
    return null;
  }
  return (
    <div className={placesCardStyle}>
      <div className="relative">
        <div className="flex h-[204px] gap-[2px]">
          <PlacesPhoto
            src={primary}
            alt={`${profile.storeName} storefront`}
            fallbackSeed={`${profile.monogram}-0`}
            className="h-full flex-[1.7]"
          />
          <div className="flex flex-1 flex-col gap-[2px]">
            {rest.map((photo, index) => (
              <PlacesPhoto
                key={photo}
                src={photo}
                alt={`${profile.storeName} photo ${index + 2}`}
                fallbackSeed={`${profile.monogram}-${index + 1}`}
                className="min-h-0 flex-1"
              />
            ))}
          </div>
        </div>
      </div>
      <div className={placesInfoStyle}>
        <p className="type-fs-paragraph text-fs-text-primary">
          {profile.storeName}
        </p>
        {profile.rating ? (
          <div className={placesRatingRowStyle}>
            <span className="type-fs-paragraph text-fs-text-primary">
              {profile.placeType}
            </span>
            <span className="type-fs-paragraph text-fs-text-primary">·</span>
            <span className="type-fs-paragraph text-fs-text-primary">
              {profile.rating.toFixed(1)}
            </span>
            <StarRating rating={profile.rating} />
            <span className="type-fs-paragraph text-fs-text-primary underline">
              ({profile.reviewCount})
            </span>
          </div>
        ) : null}
        {identityLine ? (
          <p className={placesMetadataStyle}>{identityLine}</p>
        ) : null}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Google-Places-backed business-name search. Slate's TextInput has no prefix
// slot, so the search affordance is composed here to match production's
// `PhysicalStoreSearchInput` (leading magnifier, live dropdown of listings, and
// an "Enter business name manually" escape hatch that reveals postal code).
// ---------------------------------------------------------------------------

const searchFieldStyle =
  "flex items-center gap-(--slate-dimensions-2) rounded-[4px] border px-(--slate-dimensions-3) py-[13px]";
const searchFieldRestStyle = "border-fs-border-subdued";
const searchFieldErrorStyle = "border-(--slate-color-message-border-critical)";
const searchInputStyle =
  "type-fs-paragraph text-fs-text-primary placeholder:text-fs-text-subdued w-full border-none bg-transparent outline-none";
const suggestionListStyle =
  "border-fs-border-muted bg-fs-surface-primary absolute top-[calc(100%+4px)] right-0 left-0 z-10 flex flex-col rounded-[4px] border shadow-[0_2px_10px_rgba(0,0,0,0.15)]";
const suggestionRowStyle =
  "hover:bg-fs-surface-secondary flex w-full items-center gap-(--slate-dimensions-3) px-(--slate-dimensions-3) py-[12px] text-left";
const pinStyle =
  "bg-fs-surface-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-full";

export const BusinessSearchInput: React.FC<{
  label: string;
  placeholder: string;
  value: string;
  error?: string;
  suggestions: PlacesSuggestion[];
  onChange: (value: string) => void;
  onSelectSuggestion: (suggestion: PlacesSuggestion) => void;
  onEnterManually: () => void;
}> = ({
  label,
  placeholder,
  value,
  error,
  suggestions,
  onChange,
  onSelectSuggestion,
  onEnterManually,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const showDropdown = isOpen && value.trim().length >= 2;

  return (
    <div className="flex flex-col gap-(--slate-dimensions-2)">
      <span className="type-fs-label-medium text-fs-text-primary">{label}</span>
      <div className="relative">
        <div
          className={cn(
            searchFieldStyle,
            error ? searchFieldErrorStyle : searchFieldRestStyle
          )}
        >
          <Search className="text-fs-icon-subdued h-4 w-4 shrink-0" />
          <input
            type="text"
            aria-label={label}
            className={searchInputStyle}
            placeholder={placeholder}
            value={value}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            onChange={(event) => {
              onChange(event.target.value);
              setIsOpen(true);
            }}
          />
        </div>
        {showDropdown ? (
          <div className={suggestionListStyle}>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.name}
                type="button"
                className={suggestionRowStyle}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onSelectSuggestion(suggestion);
                  setIsOpen(false);
                }}
              >
                <span className={pinStyle}>
                  <Pin className="h-4 w-4" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="type-fs-paragraph text-fs-text-primary truncate">
                    {suggestion.name}
                  </span>
                  <span className="type-fs-label text-fs-text-subdued truncate">
                    {suggestion.location}
                  </span>
                </span>
              </button>
            ))}
            <button
              type="button"
              className={suggestionRowStyle}
              onMouseDown={(event) => {
                event.preventDefault();
                onEnterManually();
                setIsOpen(false);
              }}
            >
              <span className={pinStyle}>
                <Pin className="h-4 w-4" />
              </span>
              <span className="type-fs-paragraph text-fs-text-primary underline">
                Enter business name manually
              </span>
            </button>
          </div>
        ) : null}
      </div>
      {error ? (
        <span className="type-fs-label text-(--slate-color-message-text-critical)">
          {error}
        </span>
      ) : null}
    </div>
  );
};
